import type { Account, ScopeContext } from '../model/types';
import { getAccountHolders, selectDefaultAccountHolder } from './account-holder';
import { getActiveAccountsForHolder, getAllAccounts, selectDefaultAccount } from './account';
import { DebugLogger } from '../../../shared/lib/debug';

export class ScopeManager {
  private sessionId: string;
  private scopeContext: ScopeContext = {};
  private accountHolders: any[] = []; // Cache for account holders
  private accounts: any[] = []; // Cache for accounts

  constructor(sessionId: string) {
    this.sessionId = sessionId;
  }

  async initializeScope(): Promise<ScopeContext> {
    try {
      DebugLogger.scope('Initializing scope', { sessionId: this.sessionId });
      
      // Step 1: Get all account holders for the user
      DebugLogger.scope('Step 1: Getting account holders');
      const accountHoldersResponse = await getAccountHolders(this.sessionId, 'active');
      this.accountHolders = accountHoldersResponse.holders;
      
      DebugLogger.scope('Account holders received', { 
        count: this.accountHolders.length,
        holders: this.accountHolders.map(h => ({ id: h.id, name: h.name, status: h.status }))
      });

      if (this.accountHolders.length === 0) {
        DebugLogger.scope('No account holders found, attempting to get accounts without scope');
        // User doesn't have account holder permissions, but might have direct account access
        // Try to get all accounts without scope restrictions
        try {
          const accountsResponse = await getAllAccounts(this.sessionId, 'active');
          this.accounts = accountsResponse.accounts;
          
          DebugLogger.scope('Accounts received without scope', { 
            count: this.accounts.length,
            accounts: this.accounts.map(a => ({ id: a.id, name: a.name, status: a.status }))
          });

          if (this.accounts.length === 0) {
            throw new Error('No accounts found for this user');
          }

          // Auto-select account if only one active
          const selectedAccount = selectDefaultAccount(this.accounts);
          if (selectedAccount) {
            DebugLogger.scope('Account selected (no holder context)', { 
              accountId: selectedAccount.id, 
              accountName: selectedAccount.name 
            });
            this.scopeContext.account = selectedAccount;
            this.scopeContext.scopeId = selectedAccount.id;
            this.scopeContext.scopeType = 'account';
            // Note: accountHolder is intentionally undefined in this case
          } else {
            DebugLogger.scope('No default account found, user needs to select manually (no holder context)');
          }

          return this.scopeContext;
        } catch (error) {
          DebugLogger.error('Failed to get accounts without scope', { error });
          throw new Error('No organizations or accounts found for this user');
        }
      }

      // Step 2: Select account holder (auto-select if only one active)
      DebugLogger.scope('Step 2: Selecting account holder');
      const selectedHolder = selectDefaultAccountHolder(this.accountHolders);
      
      if (!selectedHolder) {
        DebugLogger.scope('No default holder found, user needs to select manually');
        // User needs to select manually - return available holders
        this.scopeContext = {
          accountHolder: undefined,
          account: undefined,
          scopeId: undefined,
          scopeType: undefined
        };
        return this.scopeContext;
      }

      DebugLogger.scope('Account holder selected', { 
        holderId: selectedHolder.id, 
        holderName: selectedHolder.name 
      });

      this.scopeContext.accountHolder = selectedHolder;
      this.scopeContext.scopeId = selectedHolder.id;
      this.scopeContext.scopeType = 'holder';

      // Step 3: Get accounts for the selected holder
      DebugLogger.scope('Step 3: Getting accounts for holder', { holderId: selectedHolder.id });
      this.accounts = await getActiveAccountsForHolder(this.sessionId, selectedHolder.id);

      DebugLogger.scope('Accounts received', { 
        count: this.accounts.length,
        accounts: this.accounts.map(a => ({ id: a.id, name: a.name, status: a.status }))
      });

      if (this.accounts.length === 0) {
        DebugLogger.scope('No accounts found, scope set to holder level');
        // No accounts under this holder - scope is set to holder level
        return this.scopeContext;
      }

      // Step 4: Select account (auto-select if only one active)
      DebugLogger.scope('Step 4: Selecting account');
      const selectedAccount = selectDefaultAccount(this.accounts);
      
      if (selectedAccount) {
        DebugLogger.scope('Account selected', { 
          accountId: selectedAccount.id, 
          accountName: selectedAccount.name 
        });
        this.scopeContext.account = selectedAccount;
        this.scopeContext.scopeId = selectedAccount.id;
        this.scopeContext.scopeType = 'account';
      } else {
        DebugLogger.scope('No default account found, user needs to select manually');
      }

      DebugLogger.scope('Scope initialization complete', this.scopeContext);
      return this.scopeContext;
    } catch (error) {
      DebugLogger.error('Failed to initialize scope', { 
        sessionId: this.sessionId, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw new Error(`Failed to initialize scope: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async setAccountHolder(holderId: string): Promise<ScopeContext> {
    try {
      // Get accounts for the selected holder
      this.accounts = await getActiveAccountsForHolder(this.sessionId, holderId);

      // Find the holder in the cached account holders
      const selectedHolder = this.accountHolders.find(h => h.id === holderId);
      
      if (!selectedHolder) {
        throw new Error('Organization not found');
      }

      this.scopeContext.accountHolder = selectedHolder;
      this.scopeContext.scopeId = selectedHolder.id;
      this.scopeContext.scopeType = 'holder';

      // Auto-select account if only one active
      if (this.accounts.length > 0) {
        const selectedAccount = selectDefaultAccount(this.accounts);
        if (selectedAccount) {
          this.scopeContext.account = selectedAccount;
          this.scopeContext.scopeId = selectedAccount.id;
          this.scopeContext.scopeType = 'account';
        }
      } else {
        this.scopeContext.account = undefined;
      }

      return this.scopeContext;
    } catch (error) {
      throw new Error(`Failed to set organization: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async setAccount(accountId: string): Promise<ScopeContext> {
    try {
      DebugLogger.scope('Setting account', { accountId, cachedAccountsCount: this.accounts.length });
      
      // Find the account in the cached accounts
      const account = this.accounts.find(a => a.id === accountId);
      
      if (!account) {
        DebugLogger.error('Account not found in cached accounts', { 
          accountId, 
          availableAccounts: this.accounts.map(a => ({ id: a.id, name: a.name }))
        });
        throw new Error('Account not found in cached accounts');
      }
      
      DebugLogger.scope('Account found', { 
        accountId: account.id, 
        accountName: account.name, 
        holderId: account.holder_id 
      });
      
      // Try to ensure we have the correct holder context, but don't fail if holder is not available
      // This handles the case where user has direct account access without holder permissions
      if (account.holder_id) {
      if (!this.scopeContext.accountHolder || this.scopeContext.accountHolder.id !== account.holder_id) {
        DebugLogger.scope('Looking for holder', { 
          holderId: account.holder_id, 
          cachedHoldersCount: this.accountHolders.length 
        });
        
        const holder = this.accountHolders.find(h => h.id === account.holder_id);
        
          if (holder) {
            DebugLogger.scope('Holder found', { holderId: holder.id, holderName: holder.name });
            this.scopeContext.accountHolder = holder;
          } else {
            DebugLogger.scope('Holder not found in cache, but continuing without holder context', { 
              holderId: account.holder_id
            });
            // Don't throw error - user might have direct account access without holder permissions
            // accountHolder will remain undefined in this case
          }
        }
      } else {
        DebugLogger.scope('Account has no holder_id, setting account without holder context');
      }

      this.scopeContext.account = account;
      this.scopeContext.scopeId = account.id;
      this.scopeContext.scopeType = 'account';

      DebugLogger.scope('Account set successfully', this.scopeContext);
      return this.scopeContext;
    } catch (error) {
      DebugLogger.error('Failed to set account', { accountId, error });
      throw new Error(`Failed to set account: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  getCurrentScope(): ScopeContext {
    return { ...this.scopeContext };
  }

  isScopeComplete(): boolean {
    return !!(this.scopeContext.scopeId && this.scopeContext.scopeType);
  }

  needsAccountHolderSelection(): boolean {
    return !this.scopeContext.accountHolder;
  }

  needsAccountSelection(): boolean {
    return !!(this.scopeContext.accountHolder && !this.scopeContext.account);
  }

  getAccountHolders(): any[] {
    return this.accountHolders;
  }

  getAccounts(): any[] {
    return this.accounts;
  }

  async getAccountsForHolder(holderId: string): Promise<Account[]> {
    try {
      DebugLogger.scope('Getting accounts for holder', { holderId });
      this.accounts = await getActiveAccountsForHolder(this.sessionId, holderId);

      DebugLogger.scope('Accounts loaded for holder', {
        holderId,
        count: this.accounts.length,
        accounts: this.accounts.map(a => ({ id: a.id, name: a.name, status: a.status }))
      });

      return this.accounts;
    } catch (error) {
      DebugLogger.error('Failed to get accounts for holder', { holderId, error });
      throw new Error(`Failed to get accounts for organization: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Sets the account holder in the cache and context
   * This is useful when initializing from an existing context
   */
  setAccountHolderInCache(accountHolder: any): void {
    DebugLogger.scope('Setting account holder in cache', { 
      holderId: accountHolder.id, 
      holderName: accountHolder.name 
    });
    
    // Add to cache if not already present
    const existingIndex = this.accountHolders.findIndex(h => h.id === accountHolder.id);
    if (existingIndex >= 0) {
      this.accountHolders[existingIndex] = accountHolder;
    } else {
      this.accountHolders.push(accountHolder);
    }
    
    // Update context
    this.scopeContext.accountHolder = accountHolder;
    this.scopeContext.scopeId = accountHolder.id;
    this.scopeContext.scopeType = 'holder';
  }

  /**
   * Sets accounts in the cache
   * This is useful when loading accounts without holder context
   */
  setAccountsInCache(accounts: any[]): void {
    DebugLogger.scope('Setting accounts in cache', { 
      count: accounts.length,
      accounts: accounts.map(a => ({ id: a.id, name: a.name }))
    });
    
    this.accounts = accounts;
  }
}
